/**
 * Typography size configuration for PortableText components
 *
 * Configure responsive heading sizes across breakpoints (default, md, lg)
 * using design system tokens from globals.css: h1, h1-5, h2, h3, h4, h5, h6
 */

export type TypographySize = 'h1' | 'h1-5' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'

export type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'

export type SubheadingLevel =
  | 'subheading-2xl'
  | 'subheading-xl'
  | 'subheading-lg'
  | 'subheading-md'
  | 'subheading-sm'
  | 'subheading-xs'

export type ResponsiveTypographySize = {
  /** Default size (mobile/small screens) */
  default: TypographySize
  /** Medium breakpoint (768px+) */
  md: TypographySize
  /** Large breakpoint (1024px+) */
  lg: TypographySize
}

export type HeadingStyleConfig = Record<HeadingLevel, ResponsiveTypographySize>
export type SubheadingStyleConfig = Record<SubheadingLevel, ResponsiveTypographySize>

/**
 * Default heading size configuration
 *
 * Customize these values to match your design system:
 * - default: Mobile/small screens
 * - md: Tablet screens (768px+)
 * - lg: Desktop screens (1024px+)
 *
 * Available sizes from theme.css:
 * - h1, h2, h3, h4, h5, h6
 */
export const defaultHeadingConfig: HeadingStyleConfig = {
  h1: {
    default: 'h3',
    md: 'h2',
    lg: 'h1',
  },
  h2: {
    default: 'h3',
    md: 'h2',
    lg: 'h2',
  },
  h3: {
    default: 'h4',
    md: 'h3',
    lg: 'h3',
  },
  h4: {
    default: 'h4',
    md: 'h4',
    lg: 'h4',
  },
  h5: {
    default: 'h6',
    md: 'h6',
    lg: 'h5',
  },
  h6: {
    default: 'h6',
    md: 'h6',
    lg: 'h6',
  },
}

/**
 * Generate Tailwind CSS classes for a heading level
 *
 * @param level - The heading level (h1, h1-5, h2, h3, h4, h5, h6)
 * @param configOverrides - Optional partial configuration to override defaults
 * @returns Tailwind CSS class string with responsive sizing
 *
 * @example
 * getHeadingClasses('h1')
 * // Returns: "size-header-h2 md:size-header-h2 lg:size-header-h1"
 *
 * @example
 * getHeadingClasses('h2', { h2: { default: 'h5', md: 'h4', lg: 'h3' } })
 * // Only overrides h2, other headings use defaults
 */
export function getHeadingClasses(
  level: HeadingLevel,
  configOverrides?: Partial<HeadingStyleConfig>,
): string {
  const config = { ...defaultHeadingConfig, ...configOverrides }
  const sizes = config[level]

  return `size-header-${sizes.default} md:size-header-${sizes.md} lg:size-header-${sizes.lg}`
}

/**
 * Default subheading size configuration
 *
 * Subheadings render as <p> elements with subheading styling
 */
/*
export const defaultSubheadingConfig: SubheadingStyleConfig = {
  'subheading-2xl': {
    default: 'lg',
    md: 'lg',
    lg: '2xl',
  },
  'subheading-xl': {
    default: 'md',
    md: 'md',
    lg: 'xl',
  },
  'subheading-lg': {
    default: 'sm',
    md: 'sm',
    lg: 'lg',
  },
  'subheading-md': {
    default: 'md',
    md: 'md',
    lg: 'xl',
  },
  'subheading-sm': {
    default: 'xs',
    md: 'sm',
    lg: 'sm',
  },
  'subheading-xs': {
    default: 'xs',
    md: 'xs',
    lg: 'xs',
  },
}
  */

/**
 * Generate Tailwind CSS classes for a subheading level
 *
 * @param level - The subheading level
 * @param configOverrides - Optional partial configuration to override defaults
 * @returns Tailwind CSS class string with responsive sizing
 */
/*
export function getSubheadingClasses(
  level: SubheadingLevel,
  configOverrides?: Partial<SubheadingStyleConfig>,
): string {
  const config = { ...defaultSubheadingConfig, ...configOverrides }
  const sizes = config[level]

  return `size-subheading-${sizes.default} md:size-subheading-${sizes.md} lg:size-subheading-${sizes.lg}`
}
  */

/**
 * Create a custom heading configuration
 *
 * @example
 * const smallerHeadings = createHeadingConfig({
 *   h1: { default: 'h3', md: 'h2', lg: 'h1-5' },
 *   h2: { default: 'h4', md: 'h3', lg: 'h3' },
 * })
 */
export function createHeadingConfig(overrides: Partial<HeadingStyleConfig>): HeadingStyleConfig {
  return {
    ...defaultHeadingConfig,
    ...overrides,
  }
}

/**
 * Create a custom subheading configuration
 */
/*
export function createSubheadingConfig(
  overrides: Partial<SubheadingStyleConfig>,
): SubheadingStyleConfig {
  return {
    ...defaultSubheadingConfig,
    ...overrides,
  }
}
*/
